import { useState, useEffect, useRef, memo } from 'react';

import delay from '@zenbusiness/application-commons-utils/delay';
import Center from '@zenbusiness/zui/Center';
import Heading from '@zenbusiness/zui/Heading';
import Stack from '@zenbusiness/zui/Stack';
import { styled } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useBreakpoints from '@zenbusiness/zui/useBreakpoints';
import PropTypes from 'prop-types';

import { FocusGrid, ContainerGrid } from '#client/components/Grid';
import LoadingSpinner from '#client/components/LoadingSpinner';
import LoaderSteps from '#client/components/MoveMoney/InstantDepositModal/components/LoaderSteps';
import { MODAL_ACTION } from '#client/components/MoveMoney/InstantDepositModal/constants/instants';
import { ASTRA_STATUS } from '#client/constants/astra';
import useCreateInstantFundingAccount from '#client/hooks/useCreateInstantFundingAccount';
import useFormStep from '#client/hooks/useFormStep';

import { SummaryFormWrapper, SpinnerContainer } from './Common.styles';
import { DialogContentStyled } from './SettingUpAccount.styles';

const Wrapper = styled(Stack)(SummaryFormWrapper);

const STATUS = {
  INITIATING: 'initiating',
  ACTIVE: 'active',
  COMPLETED: 'completed'
};

const settingUpAccountSteps = [
  { name: 'Initiating contact...', status: STATUS.INITIATING, isVisible: true, isActive: false },
  { name: 'Verifying details...', status: STATUS.ACTIVE, isVisible: false, isActive: false },
  { name: 'Finalizing account...', status: STATUS.COMPLETED, isVisible: false, isActive: false }
];

const SettingUpAccount = ({ actionsKit }) => {
  const {
    sizes: { isMobile }
  } = useBreakpoints();

  const { dispatchDepositData } = useFormStep();

  const { createInstantFundingAccount } = useCreateInstantFundingAccount();
  const [status, setStatus] = useState('');

  const currentStatusIndex = useRef(0);
  const intervalIdRef = useRef(null);

  /**
   * Creates an instant funding account and waits for the response.
   * If the response is successful, it clears the interval and triggers the
   * instant deposit flow.
   * If the response is an error, it clears the interval and opens the InstantDepositModal
   * with the ACCOUNT_COULD_NOT_SET_UP step.
   */
  const setupInstantFundingAccount = async () => {
    const response = await createInstantFundingAccount();
    // If the response is successful, clear the interval and trigger the instant deposit flow
    if (ASTRA_STATUS.ACTIVE === response?.data?.createInstantFundingAccount?.accountStatus) {
      setStatus(STATUS.COMPLETED);
      clearInterval(intervalIdRef);
      // Wait a second before triggering the instant deposit flow
      // This is to give the user time to see the success message
      delay(() => actionsKit?.triggerInstantDepositFlow(), 1000);
      return;
    }

    // If the response is an error, clear the interval and open the InstantDepositModal
    if (response?.error || (status === STATUS.COMPLETED && !response)) {
      clearInterval(intervalIdRef);
      dispatchDepositData({
        type: MODAL_ACTION.GO_TO_ACCOUNT_COULD_NOT_SET_UP
      });
    }
  };

  /**
   * Updates the status of the setting up account process at intervals.
   * It checks if the current index is less than or equal to the length of the
   * settingUpAccountSteps array. If it is, it sets the status to the current step
   * and increments the current index. If it is not, it clears the interval.
   * The interval is set to 8 seconds.
   */
  const updateStatusAtIntervals = () => {
    intervalIdRef.current = setInterval(() => {
      if (currentStatusIndex.current <= settingUpAccountSteps.length - 1) {
        setStatus(settingUpAccountSteps[currentStatusIndex.current].status);
        currentStatusIndex.current += 1;
      } else {
        clearInterval(intervalIdRef);
      }
    }, 8000); // Change status every 8 seconds
  };

  useEffect(() => {
    // Start the account creation process
    setupInstantFundingAccount();

    // Start the interval-based status update
    updateStatusAtIntervals();

    return () => clearInterval(intervalIdRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // If we've reached the last step, stop the interval
    if (currentStatusIndex.current > settingUpAccountSteps.length - 1) {
      clearInterval(intervalIdRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStatusIndex.current]);

  return (
    <DialogContentStyled>
      <ContainerGrid>
        <FocusGrid>
          <Wrapper>
            <Center mt="40px">
              <Stack>
                <SpinnerContainer>
                  <LoadingSpinner />
                </SpinnerContainer>
                <Stack
                  mt={{ xs: '16px', md: '24px' }}
                  spacing="4px"
                >
                  <Heading
                    align="center"
                    family="secondary"
                    size={isMobile ? 'xl' : '2xl'}
                    weight="bold"
                  >
                    Setting up your account...
                  </Heading>

                  <Text
                    align="center"
                    color="secondary"
                    size={isMobile ? 'md' : 'lg'}
                  >
                    Please wait while we contact our partner, Astra.
                  </Text>
                </Stack>
              </Stack>
              <Stack
                gap="16px"
                mt="16px"
              >
                <LoaderSteps
                  status={status}
                  steps={settingUpAccountSteps}
                />
              </Stack>
            </Center>
          </Wrapper>
        </FocusGrid>
      </ContainerGrid>
    </DialogContentStyled>
  );
};

SettingUpAccount.propTypes = {
  actionsKit: PropTypes.shape({
    triggerInstantDepositFlow: PropTypes.func
  })
};

export default memo(SettingUpAccount);
